import React, { useEffect, useState } from 'react'
import GridSpinner from 'components/UI/GridSpinner'
import PropTypes from 'prop-types'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Container, Grid, NetworkIcon, Typography } from 'gu-ui-kit'
import clsx from 'clsx'
import styles from 'modules/Agency/agency.module.scss'
import formatMoney from 'utils/formatMoney'

const networkIconNamesMap = {
  yd: 'yandex',
}

const AccountRow = ({ row, keyRowPrefix, emptyRowsPlaceholder }) => {
  const breakpoints = useBreakpoint()

  const [columnVisibility, setColumnVisibility] = useState({})

  useEffect(() => {
    switch (true) {
      case breakpoints.sm:
        setColumnVisibility({
          expander: false,
          priority: false,
          status: false,
          actions: false,
        })
        break
      case breakpoints.fb:
        setColumnVisibility({ status: false })
        break
      default:
        setColumnVisibility({})
        break
    }
  }, [breakpoints])

  const table = useReactTable({
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
    data: row.original.subRows,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility,
    },
    columns: [
      { id: 'expander', size: 0 },
      { id: 'priority', size: 0 },
      {
        id: 'name',
        colSpan: breakpoints.sm ? 3 : 1,
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              type, // eslint-disable-next-line react/prop-types
              login,
              name,
            },
          },
        }) => (
          <Grid
            wrap={'nowrap'}
            justifyContent="start"
            alignItems="center"
            gap={breakpoints.sm ? '20px' : '32px'}
          >
            <NetworkIcon
              name={networkIconNamesMap[type] ?? type}
              size={'m'}
              width={36}
              rounded
            />
            <Container
              className={clsx(
                styles['clients-list__recommendation'],
                styles['clients-list__col-name'],
              )}
              direction="column"
              flexAlign="flex-start"
              flexJustify="flex-start"
            >
              <Typography
                variant={'p9'}
                noMargin
                className={styles['client__name']}
              >
                {name ?? login}
              </Typography>
              <Typography
                variant={'p11'}
                noMargin
                color={'text-secondary'}
                className={styles['client__email']}
                title={login}
              >
                {login}
              </Typography>
            </Container>
          </Grid>
        ),
      },
      { id: 'status' },
      {
        id: 'balance',
        colSpan: 1,
        className: clsx(styles['clients-list__cell']),
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              id,
              // eslint-disable-next-line react/prop-types
              balance: { amount, currency },
            },
          },
        }) => (
          // eslint-disable-next-line react/prop-types
          <Container
            className={styles['clients-list__col-balance']}
            flexAlign={breakpoints.sm ? 'flex-end' : 'flex-start'}
          >
            {/* eslint-disable-next-line react/prop-types */}
            <span
              key={'balance-' + id}
              dangerouslySetInnerHTML={{
                __html: formatMoney(amount, currency),
              }}
            />
          </Container>
        ),
      },
      { id: 'actions' },
    ],
  })

  return row.original.subRows.length > 0 ? (
    table.getRowModel().rows.map((row, i, array) => (
      <tr
        role={'row'}
        key={keyRowPrefix + '-' + i}
        className={clsx('subrow', {
          'subrow-last': i === array.length - 1,
        })}
      >
        {row.getVisibleCells().map((cell) => (
          <td
            colSpan={
              cell.column.columnDef.colSpan === 1
                ? ''
                : cell.column.columnDef.colSpan
            }
            key={'tbody-row-' + row.id + '-cell-' + cell.id}
            role={'cell'}
            style={{
              width:
                cell.column.getSize() !== 0 ? cell.column.getSize() : undefined,
            }}
            className={cell.column.columnDef.className}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    ))
  ) : (
    <tr role={'row'} className={'subrow subrow-last'}>
      <td colSpan={row.getVisibleCells().length} role={'cell'}>
        {row.original.subRowsLoading ? (
          <GridSpinner width={16} />
        ) : (
          emptyRowsPlaceholder
        )}
      </td>
    </tr>
  )
}

AccountRow.propTypes = {
  row: PropTypes.object.isRequired,
  keyRowPrefix: PropTypes.string,
  emptyRowsPlaceholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
}

export default AccountRow
